/**
 * Fidelizador por tenant (ADR-0025, F1): cliente de `/admin/tenants/:tenantId/fidelizador/*` y de las rutas de plataforma
 * (`/admin/platform/fidelizador/credentials`, `/admin/platform/tenants/:tenantId/fidelizador/features/*`). Tipos = espejo de
 * `apps/api/src/schemas/fidelizador.ts`. NINGÚN tipo incluye secretos: de cada uno solo `set` y `last4`.
 *
 * Secretos: viajan SOLO en el cuerpo del PUT (cuenta, rotar, credenciales compartidas). Las mutaciones que los llevan usan `gcTime: 0`
 * y quien las llama hace `reset()` al terminar para no conservar `variables`. Nunca en localStorage, URL ni caché de Query.
 */
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export type FidelizadorMode = 'own' | 'shared';
export type FidelizadorTransport = 'api' | 'smtp_relay';
export type FidelizadorAccountStatus = 'unverified' | 'verified' | 'error';
export type FidelizadorSecretField = 'api_key' | 'smtp_user' | 'smtp_password' | 'webhook_secret';
export type FeatureContractStatus = 'active' | 'paused' | 'cancelled';

export const FIDELIZADOR_SECRET_FIELDS: readonly FidelizadorSecretField[] = [
  'api_key',
  'smtp_user',
  'smtp_password',
  'webhook_secret',
];

export interface SecretState {
  set: boolean;
  last4: string | null;
}

export interface FidelizadorAccount {
  id: string;
  mode: FidelizadorMode;
  transport: FidelizadorTransport;
  instanceSlug: string | null;
  fromEmail: string | null;
  senderId: string | null;
  credentialsSource: 'tenant' | 'platform';
  secrets: Record<FidelizadorSecretField, SecretState>;
  webhookId: string | null;
  hasRouteKey: boolean;
  status: FidelizadorAccountStatus;
  lastCheckedAt: string | null;
  lastCheckCode: string | null;
  enabled: boolean;
  /** Mapa finalidad → ids de término y finalidad de Fidelizador (F3). */
  consentMap: FidelizadorConsentMap;
  createdAt: string;
  updatedAt: string;
}

export type FidelizadorConsentMap = Record<string, { termId: number; purposeId: number }>;

export interface FidelizadorCredentialsInput {
  apiKey?: string;
  smtpUser?: string;
  smtpPassword?: string;
  webhookSecret?: string;
}

export interface PutFidelizadorInput {
  mode?: FidelizadorMode;
  transport?: FidelizadorTransport;
  instanceSlug?: string | null;
  fromEmail?: string | null;
  senderId?: string | null;
  credentials?: FidelizadorCredentialsInput;
}

export interface FidelizadorTestResult {
  ok: boolean;
  /** Código fijo; el texto lo pone el panel (`features/fidelizador/labels.ts`), nunca el proveedor. */
  code: string;
  status: FidelizadorAccountStatus;
  checkedAt: string;
}

/** Resultado de «Probar webhook»: código HTTP de la entrega, duración y causa FIJA; el texto lo pone el panel. Un fallo de entrega es `ok: false` (no un error HTTP). */
export interface FidelizadorWebhookTestResult {
  ok: boolean;
  statusCode: number | null;
  durationMs: number;
  error: 'timeout' | 'connection_failed' | 'blocked' | null;
}

export type CanEnable = { ok: true } | { ok: false; reason: string; message: string; missing: string[] };
export type CanDisable = { ok: true } | { ok: false; reason: string; message: string; dependents: string[] };

export interface FidelizadorFeature {
  code: string;
  name: string;
  description: string;
  phase: string;
  dependsOn: string[];
  catalogStatus: string;
  contractStatus: FeatureContractStatus | null;
  pausedBy: 'tenant' | 'platform' | null;
  canEnable: CanEnable;
  canDisable: CanDisable;
  capUnit: string;
  dailyCapSends: number | null;
  monthlyCapSends: number | null;
}

export interface FidelizadorSharedStatus {
  configured: boolean;
  last4: string | null;
  instanceSlug: string | null;
  hasSmtp: boolean;
  hasWebhookSecret: boolean;
  /** Webhook ÚNICO de la cuenta compartida (ADR-0030): solo estado y últimos 4 del secreto; la clave de ruta y el secreto nunca llegan al panel. */
  webhook: { registered: boolean; last4: string | null; registeredAt: string | null };
  updatedAt: string | null;
}

export interface PutSharedInput {
  apiKey?: string;
  smtpUser?: string;
  smtpPassword?: string;
  webhookSecret?: string;
  instanceSlug?: string;
}

const base = (tenantId: string) => `/admin/tenants/${tenantId}/fidelizador`;
const platformFeatures = (tenantId: string) => `/admin/platform/tenants/${tenantId}/fidelizador/features`;

export const fidelizadorKey = (tenantId: string) => ['tenants', tenantId, 'fidelizador'] as const;
export const SHARED_KEY = ['platform', 'fidelizador', 'credentials'] as const;
export const platformFeaturesKey = (tenantId: string) =>
  ['platform', 'tenants', tenantId, 'fidelizador', 'features'] as const;

export const fidelizadorAccountQuery = (tenantId: string) =>
  queryOptions({
    queryKey: [...fidelizadorKey(tenantId), 'account'] as const,
    queryFn: () => api.get<{ account: FidelizadorAccount | null }>(base(tenantId)),
    select: (d) => d.account,
  });

export const fidelizadorFeaturesQuery = (tenantId: string) =>
  queryOptions({
    queryKey: [...fidelizadorKey(tenantId), 'features'] as const,
    queryFn: () => api.get<{ items: FidelizadorFeature[] }>(`${base(tenantId)}/features`),
    select: (d) => d.items,
  });

export const platformFidelizadorFeaturesQuery = (tenantId: string) =>
  queryOptions({
    queryKey: platformFeaturesKey(tenantId),
    queryFn: () => api.get<{ items: FidelizadorFeature[] }>(platformFeatures(tenantId)),
    select: (d) => d.items,
  });

export const fidelizadorSharedQuery = queryOptions({
  queryKey: SHARED_KEY,
  queryFn: () => api.get<FidelizadorSharedStatus>('/admin/platform/fidelizador/credentials'),
});

/** ¿La plataforma ya registró el webhook de la cuenta compartida? (solo un booleano; lo lee el tenant en modo `shared`). */
export const fidelizadorSharedWebhookQuery = (tenantId: string) =>
  queryOptions({
    queryKey: [...fidelizadorKey(tenantId), 'shared-webhook'] as const,
    queryFn: () => api.get<{ registered: boolean }>(`${base(tenantId)}/shared-webhook`),
    select: (d) => d.registered,
  });

function useInvalidate(tenantId: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: fidelizadorKey(tenantId) });
}

/** Crear o editar la cuenta. Lleva secretos: `gcTime: 0` y `reset()` en quien la usa. */
export function usePutFidelizador(tenantId: string) {
  const invalidate = useInvalidate(tenantId);
  return useMutation({
    gcTime: 0,
    mutationFn: (input: PutFidelizadorInput) =>
      api.put<{ created: boolean; account: FidelizadorAccount }>(base(tenantId), input),
    onSuccess: invalidate,
  });
}

/** «Hablar con el agente» en los correos (ADR-0026): espejo de `reopenSettingsSchema`. Sin datos de personas ni tokens. */
export interface ReopenSettings {
  serviceActive: boolean;
  campaignButton: boolean;
  reservationReminderButton: boolean;
  channel: 'widget' | 'whatsapp' | null;
  bindingId: string | null;
  widgetUrl: string | null;
  ttlDays: number;
  campaignDestinationReady: boolean;
}
export type ReopenSettingsPatch = Partial<
  Pick<
    ReopenSettings,
    'campaignButton' | 'reservationReminderButton' | 'channel' | 'bindingId' | 'widgetUrl' | 'ttlDays'
  >
>;

export const reopenSettingsQuery = (tenantId: string) =>
  queryOptions({
    queryKey: [...fidelizadorKey(tenantId), 'reopen'] as const,
    queryFn: () => api.get<ReopenSettings>(`${base(tenantId)}/reopen`),
  });

export function usePutReopenSettings(tenantId: string) {
  const invalidate = useInvalidate(tenantId);
  return useMutation({
    mutationFn: (patch: ReopenSettingsPatch) => api.put<ReopenSettings>(`${base(tenantId)}/reopen`, patch),
    onSuccess: invalidate,
  });
}

/** Espera y vigencia del correo de valoración (ADR-0027/0029): espejo de `postExperienceSettingsSchema`. Sin datos de personas ni tokens. */
export interface PostExperienceSettings {
  sessionDelayHours: number;
  reservationDelayHours: number;
  ttlDays: number;
  serviceActive: boolean;
  /** `false` = valores por omisión (nunca se ajustó). */
  customized: boolean;
}
export type PostExperienceSettingsPatch = Partial<
  Pick<PostExperienceSettings, 'sessionDelayHours' | 'reservationDelayHours' | 'ttlDays'>
>;

export const postExperienceSettingsQuery = (tenantId: string) =>
  queryOptions({
    queryKey: [...fidelizadorKey(tenantId), 'post-experience'] as const,
    queryFn: () => api.get<PostExperienceSettings>(`${base(tenantId)}/post-experience`),
  });

export function usePutPostExperienceSettings(tenantId: string) {
  const invalidate = useInvalidate(tenantId);
  return useMutation({
    mutationFn: (patch: PostExperienceSettingsPatch) =>
      api.put<PostExperienceSettings>(`${base(tenantId)}/post-experience`, patch),
    onSuccess: invalidate,
  });
}

/** Reemplaza el mapa de finalidades de consentimiento (ids de Fidelizador; sin datos personales ni secretos). */
export function usePutFidelizadorConsentMap(tenantId: string) {
  const invalidate = useInvalidate(tenantId);
  return useMutation({
    mutationFn: (map: FidelizadorConsentMap) =>
      api.put<{ consentMap: FidelizadorConsentMap }>(`${base(tenantId)}/consent-map`, { map }),
    onSuccess: invalidate,
  });
}

export function useDeleteFidelizador(tenantId: string) {
  const invalidate = useInvalidate(tenantId);
  return useMutation({ mutationFn: () => api.del<{ ok: true }>(base(tenantId)), onSuccess: invalidate });
}

/** Rotar UN secreto de la cuenta propia. Lleva el valor: `gcTime: 0` y `reset()` en quien la usa. */
export function useRotateFidelizadorSecret(tenantId: string) {
  const invalidate = useInvalidate(tenantId);
  return useMutation({
    gcTime: 0,
    mutationFn: ({ field, value }: { field: FidelizadorSecretField; value: string }) =>
      api.put<{ account: FidelizadorAccount | null }>(`${base(tenantId)}/secrets/${field}`, { value }),
    onSuccess: invalidate,
  });
}

export function useTestFidelizador(tenantId: string) {
  const invalidate = useInvalidate(tenantId);
  return useMutation({
    mutationFn: () => api.post<FidelizadorTestResult>(`${base(tenantId)}/test`),
    onSuccess: invalidate,
  });
}

export function useSetFidelizadorEnabled(tenantId: string) {
  const invalidate = useInvalidate(tenantId);
  return useMutation({
    mutationFn: (enabled: boolean) =>
      api.post<{ account: FidelizadorAccount | null }>(`${base(tenantId)}/${enabled ? 'resume' : 'pause'}`),
    onSuccess: invalidate,
  });
}

/** El tenant pausa o reanuda lo ya contratado (no contrata: eso lo hace la plataforma). */
export function useTenantFeatureAction(tenantId: string) {
  const invalidate = useInvalidate(tenantId);
  return useMutation({
    mutationFn: ({ code, verb }: { code: string; verb: 'pause' | 'resume' }) =>
      api.post<{ code: string; status: FeatureContractStatus }>(`${base(tenantId)}/features/${code}/${verb}`),
    onSuccess: invalidate,
  });
}

/* ------------------------------------------------------------------ Plataforma */

export function usePutSharedFidelizador() {
  const qc = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: (input: PutSharedInput) =>
      api.put<FidelizadorSharedStatus>('/admin/platform/fidelizador/credentials', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: SHARED_KEY }),
  });
}

export function useDeleteSharedFidelizador() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<{ ok: true }>('/admin/platform/fidelizador/credentials'),
    onSuccess: () => qc.invalidateQueries({ queryKey: SHARED_KEY }),
  });
}

export function usePlatformFeatureAction(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, verb }: { code: string; verb: 'enable' | 'pause' }) =>
      api.post<{ code: string; status: FeatureContractStatus }>(`${platformFeatures(tenantId)}/${code}/${verb}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: platformFeaturesKey(tenantId) }),
  });
}

export function usePlatformFeatureCaps(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, ...caps }: { code: string; dailyCapSends: number | null; monthlyCapSends: number | null }) =>
      api.put<{ code: string; status: FeatureContractStatus }>(`${platformFeatures(tenantId)}/${code}/caps`, caps),
    onSuccess: () => qc.invalidateQueries({ queryKey: platformFeaturesKey(tenantId) }),
  });
}

/** Borrar UN secreto de la cuenta propia. */
export function useDeleteFidelizadorSecret(tenantId: string) {
  const invalidate = useInvalidate(tenantId);
  return useMutation({
    mutationFn: (field: FidelizadorSecretField) =>
      api.del<{ account: FidelizadorAccount | null }>(`${base(tenantId)}/secrets/${field}`),
    onSuccess: invalidate,
  });
}

// Webhook de eventos de entrega: la clave de ruta y el secreto `whsec_` nunca llegan al panel (la cuenta solo trae `webhookId`, `hasRouteKey` y `••••last4`).
export function useCreateFidelizadorWebhook(tenantId: string) {
  const invalidate = useInvalidate(tenantId);
  return useMutation({
    mutationFn: () => api.post<{ account: FidelizadorAccount }>(`${base(tenantId)}/webhook`),
    onSuccess: invalidate,
  });
}

export function useRotateFidelizadorWebhookSecret(tenantId: string) {
  const invalidate = useInvalidate(tenantId);
  return useMutation({
    mutationFn: () => api.post<{ account: FidelizadorAccount }>(`${base(tenantId)}/webhook/rotate-secret`),
    onSuccess: invalidate,
  });
}

export function useTestFidelizadorWebhook(tenantId: string) {
  return useMutation({
    mutationFn: () => api.post<FidelizadorWebhookTestResult>(`${base(tenantId)}/webhook/test`),
  });
}

export function useDeleteFidelizadorWebhook(tenantId: string) {
  const invalidate = useInvalidate(tenantId);
  return useMutation({
    mutationFn: () => api.del<{ account: FidelizadorAccount }>(`${base(tenantId)}/webhook`),
    onSuccess: invalidate,
  });
}

// Webhook ÚNICO de la cuenta compartida (solo platform admin; ADR-0030). Las respuestas son el estado de la cuenta compartida: ni la clave de ruta ni el secreto `whsec_` llegan al panel.
const SHARED_WEBHOOK = '/admin/platform/fidelizador/webhook';

export function useCreateSharedWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<FidelizadorSharedStatus>(SHARED_WEBHOOK),
    onSuccess: () => qc.invalidateQueries({ queryKey: SHARED_KEY }),
  });
}

export function useRotateSharedWebhookSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<FidelizadorSharedStatus>(`${SHARED_WEBHOOK}/rotate-secret`),
    onSuccess: () => qc.invalidateQueries({ queryKey: SHARED_KEY }),
  });
}

export function useTestSharedWebhook() {
  return useMutation({ mutationFn: () => api.post<FidelizadorWebhookTestResult>(`${SHARED_WEBHOOK}/test`) });
}

export function useDeleteSharedWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<FidelizadorSharedStatus>(SHARED_WEBHOOK),
    onSuccess: () => qc.invalidateQueries({ queryKey: SHARED_KEY }),
  });
}
